import React, { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { ProductAttributesInput } from "../ProductAttributes/ProductAttributesInput";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { InstallAgeSection } from "./InstallAgeFilter/InstallAgeSection";
import { FilterLayout } from "./FilterLayout";
import CollectionSelect from "../../CollectionSelect/CollectionSelect";
import { Stack } from "@mui/material";

export const ProductAttributesFilter: React.FC = React.memo(() => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Get initial selected items from draftFilters
  const selectedProductAttributes = useMemo(
    () => draftFilters?.productAttributes?.ids.map((id) => id.toString()) || [],
    [draftFilters?.productAttributes?.ids]
  );

  // Handle product attributes selection change
  const handleProductAttributesSelect = useCallback(
    (productAttributesIds: string[]) => {
      if (productAttributesIds?.length === 0) {
        setDraftFilters((prevFilters) => ({
          ...prevFilters,
          productAttributes: undefined,
        }));
        return;
      }

      const ids = productAttributesIds.map(Number);
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        productAttributes: {
          ...prevFilters?.productAttributes,
          ids: ids.length ? ids : [],
        },
      }));
    },
    [setDraftFilters]
  );

  // Handle clearing the filter
  const handleClearFilter = useCallback(() => {
    setDraftFilters((prevFilters) => ({
      ...prevFilters,
      productAttributes: undefined,
    }));
  }, [setDraftFilters]);

  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        productAttributes: {
          ...prevFilters?.productAttributes,
          ids: prevFilters?.productAttributes?.ids || [],
          installAgeMonths:
            installAge.min === undefined && installAge.max === undefined
              ? undefined
              : installAge,
        },
      }));
    },
    [setDraftFilters]
  );

  return (
    <FilterLayout
      title="Product Attributes"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      refProp={elementRef}
      id="product-attributes-filter"
    >
      <Box marginBottom={1}>
        <Typography variant="body2">
          This filter helps you find companies using products with specific
          attributes, ranging from AI capabilities to electronic services.
        </Typography>
      </Box>

      <Stack direction="column" gap={1}>
        <ProductAttributesInput
          selectedItems={selectedProductAttributes}
          onProductAttributesSelect={handleProductAttributesSelect}
          id="user-product-attributes-input"
        />

        <CollectionSelect
          placeholder="Search Collections"
          type="product_attribute"
          onSelect={({ ids }) =>
            handleProductAttributesSelect([
              ...selectedProductAttributes,
              ...(ids as string[]).filter(
                (id) => !selectedProductAttributes.includes(id)
              ),
            ])
          }
        />
      </Stack>

      <InstallAgeSection
        disabled={selectedProductAttributes.length === 0}
        value={
          draftFilters?.productAttributes?.installAgeMonths || {
            min: undefined,
            max: undefined,
          }
        }
        onChange={handleInstallAgeChange}
        filterHasSelection={selectedProductAttributes.length > 0}
        sx={{ mb: 1 }}
      />
    </FilterLayout>
  );
});
